import { useSyncExternalStore } from "react";
import { pushWorldPref } from "./prefs.js";

/**
 * The game-mode layout of the Dünya page: immersive (the 3D view fills the page, the details panel
 * and the app menu are drawers) or docked (the classic columns). Fullscreen is a transient mode on
 * top: while it is on, the layout is always immersive, and leaving it restores the saved layout.
 * Pure reducer + a tiny store, so the state machine is unit tested without a DOM.
 */
export type WorldLayout = "immersive" | "docked";
/** `native`: the Fullscreen API. `pseudo`: browsers without it (iPhone Safari): immersive + a fixed page. */
export type FsMode = "off" | "native" | "pseudo";

export interface LayoutState {
  layout: WorldLayout;
  /** The details drawer (immersive only). */
  panelOpen: boolean;
  /** The app menu drawer (immersive only). */
  navOpen: boolean;
  fs: FsMode;
  /** False when the browser cannot draw the scene: the panel is then the only way in, so docked. */
  webgl: boolean;
}

export type LayoutAction =
  | { type: "setLayout"; layout: WorldLayout }
  | { type: "togglePanel" }
  | { type: "openPanel" }
  | { type: "closePanel" }
  | { type: "toggleNav" }
  | { type: "closeNav" }
  | { type: "fullscreen"; mode: FsMode }
  | { type: "webgl"; ok: boolean };

export const INITIAL_LAYOUT: LayoutState = {
  layout: "immersive",
  panelOpen: false,
  navOpen: false,
  fs: "off",
  webgl: true,
};

/** What the page actually shows: fullscreen forces immersive. */
export const effectiveLayout = (s: LayoutState): WorldLayout =>
  !s.webgl ? "docked" : s.fs !== "off" ? "immersive" : s.layout;

export function layoutReducer(s: LayoutState, a: LayoutAction): LayoutState {
  switch (a.type) {
    case "setLayout":
      return { ...s, layout: a.layout, panelOpen: false, navOpen: false };
    case "togglePanel":
      // Docked: the panel is a column, so "toggle" means going immersive (panel away).
      if (effectiveLayout(s) === "docked") return { ...s, layout: "immersive", panelOpen: false };
      return { ...s, panelOpen: !s.panelOpen, navOpen: false };
    case "openPanel":
      return effectiveLayout(s) === "docked" ? s : { ...s, panelOpen: true, navOpen: false };
    case "closePanel":
      return s.panelOpen ? { ...s, panelOpen: false } : s;
    case "toggleNav":
      return { ...s, navOpen: !s.navOpen, panelOpen: false };
    case "closeNav":
      return s.navOpen ? { ...s, navOpen: false } : s;
    case "webgl":
      return a.ok === s.webgl ? s : { ...s, webgl: a.ok, panelOpen: false, navOpen: false };
    case "fullscreen":
      return a.mode === s.fs ? s : { ...s, fs: a.mode, navOpen: false };
  }
}

export const LAYOUT_STORAGE_KEY = "uz.world.layout";

export const isLayout = (v: unknown): v is WorldLayout => v === "immersive" || v === "docked";

export function storedLayout(): WorldLayout | undefined {
  try {
    const v = globalThis.localStorage?.getItem(LAYOUT_STORAGE_KEY);
    return isLayout(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

export class LayoutStore {
  private state: LayoutState;
  private listeners = new Set<() => void>();
  /** The element fullscreen is requested on (the world page: canvas, HUD, dock and drawers). */
  private root: HTMLElement | null = null;

  constructor(initial: Partial<LayoutState> = {}) {
    this.state = { ...INITIAL_LAYOUT, layout: storedLayout() ?? "immersive", ...initial };
  }

  setRoot(el: HTMLElement | null): void {
    this.root = el;
  }

  getState = (): LayoutState => this.state;
  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  dispatch(action: LayoutAction): void {
    const next = layoutReducer(this.state, action);
    if (next === this.state) return;
    const layoutChanged = next.layout !== this.state.layout;
    this.state = next;
    if (layoutChanged) this.remember(next.layout);
    for (const l of this.listeners) l();
  }

  /** Takes the layout the server (or another device) has; never pushed back. */
  adopt(layout: WorldLayout | undefined): void {
    if (!layout || layout === this.state.layout) return;
    try {
      globalThis.localStorage?.setItem(LAYOUT_STORAGE_KEY, layout);
    } catch {
      // Blocked storage: the layout still applies for this visit.
    }
    this.state = { ...this.state, layout };
    for (const l of this.listeners) l();
  }

  private remember(layout: WorldLayout): void {
    try {
      globalThis.localStorage?.setItem(LAYOUT_STORAGE_KEY, layout);
    } catch {
      // Blocked storage.
    }
    pushWorldPref({ layout });
  }

  // ------------------------------------------------------------------ fullscreen

  /** Browser fullscreen of an element, or the pseudo mode where the API does not exist. */
  async enterFullscreen(el: HTMLElement | null, doc: Document = document): Promise<void> {
    const target = el as
      | (HTMLElement & {
          requestFullscreen?: (o?: FullscreenOptions) => Promise<void>;
          webkitRequestFullscreen?: () => Promise<void> | void;
        })
      | null;
    const request = target?.requestFullscreen ?? target?.webkitRequestFullscreen;
    if (!target || !request || doc.fullscreenEnabled === false) {
      this.dispatch({ type: "fullscreen", mode: "pseudo" });
      hideAddressBar();
      return;
    }
    try {
      await request.call(target);
      // The `fullscreenchange` event moves the state to native.
    } catch {
      this.dispatch({ type: "fullscreen", mode: "pseudo" });
    }
  }

  async exitFullscreen(doc: Document = document): Promise<void> {
    if (this.state.fs === "pseudo") {
      this.dispatch({ type: "fullscreen", mode: "off" });
      return;
    }
    if (doc.fullscreenElement) {
      try {
        await doc.exitFullscreen();
      } catch {
        // Already out.
      }
    }
    this.dispatch({ type: "fullscreen", mode: "off" });
  }

  async toggleFullscreen(el?: HTMLElement | null, doc: Document = document): Promise<void> {
    if (this.state.fs !== "off" || doc.fullscreenElement) await this.exitFullscreen(doc);
    else await this.enterFullscreen(el === undefined ? this.root : el, doc);
  }

  /** Follows the browser: Esc or the system gesture leaves fullscreen without our button. */
  onFullscreenChange(doc: Document = document): void {
    if (doc.fullscreenElement) this.dispatch({ type: "fullscreen", mode: "native" });
    else if (this.state.fs === "native") this.dispatch({ type: "fullscreen", mode: "off" });
  }
}

/** The iOS trick: scrolling a pixel hides the Safari address bar on a tall page. */
function hideAddressBar(): void {
  try {
    globalThis.setTimeout(() => globalThis.scrollTo?.(0, 1), 50);
  } catch {
    // No window.
  }
}

export const layoutStore = new LayoutStore();

export function useLayout(store: LayoutStore = layoutStore): LayoutState {
  return useSyncExternalStore(store.subscribe, store.getState);
}
